/**
 * Login Page Component for NGUI Voice Interface
 * Glass morphism design with Tailwind CSS
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTenant, useBrandName, useBrandFullName } from '../contexts/TenantContext';
import { useDisplayMode } from '../contexts/DisplayModeContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { login, isLoading } = useAuth();
  const { loading: tenantLoading } = useTenant();
  const { isFeatureEnabled: einkFeatureEnabled, isEInkMode, setEInkMode } = useDisplayMode();
  const brandName = useBrandName();
  const brandFullName = useBrandFullName();

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Check if redirected due to session expiration
  const sessionExpired = new URLSearchParams(window.location.search).get('session_expired') === 'true';

  // Show loading spinner while fetching tenant branding
  if (tenantLoading) {
    return (
      <div className="w-full flex items-center justify-center bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
          <span className="text-gray-500 text-sm">{t('loading')}</span>
        </div>
      </div>
    );
  }

  /**
   * Handle login form submission
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError(t('form.validation.required'));
      return;
    }

    try {
      await login(username, password);
      // Redirect to voice interface on success
      navigate('/voice');
    } catch (err: unknown) {
      // Extract user-friendly error message from API response
      const axiosError = err as { response?: { data?: { detail?: string } } };
      const apiMessage = axiosError?.response?.data?.detail;

      if (apiMessage) {
        setError(apiMessage);
      } else if (err instanceof Error) {
        setError(err.message || t('messages.loginFailed'));
      } else {
        setError(t('messages.loginFailed'));
      }
    }
  };

  return (
    <div className="w-full flex items-center justify-center grid-pattern bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Glass Pane Container */}
      <div className="glass-pane px-12 py-16 max-w-md w-full mx-4">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">{brandName}</h1>
          <p className="text-gray-600">{brandFullName}</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Session Expired Message */}
          {sessionExpired && !error && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('messages.sessionExpired')}</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Username Input */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.username.label')}
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
              placeholder={t('form.username.placeholder')}
              disabled={isLoading}
              autoComplete="username"
            />
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.password.label')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder={t('form.password.placeholder')}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full btn-gradient-blue"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                {t('form.submit.loading')}
              </span>
            ) : (
              t('form.submit.default')
            )}
          </button>
        </form>

        {/* E-Ink Mode Toggle - Only shown if tenant has feature enabled */}
        {einkFeatureEnabled && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('displayOptions.einkMode')}</span>
                  <p className="text-xs text-gray-500">{t('displayOptions.einkModeDescription')}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEInkMode(!isEInkMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${
                  isEInkMode ? 'bg-black' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={isEInkMode}
                aria-label={t('displayOptions.einkMode')}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isEInkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            {t('footer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
